import React, { useState, useEffect } from 'react';
import Row from '@amzn/meridian/row';
import SearchField, {
  SearchSuggestion,
  SearchSuggestionHeader,
} from '@amzn/meridian/search-field';
import { useTranslation } from 'src/arb-wrapper';
import { TagCloud } from '../TagCloud';
import {
  type Filter,
  SCOPE_TERM_DELIMITER,
  deserializeFilter,
  serializeFilters,
} from './filter';
import { FILTERS_STORAGE_KEY } from 'src/constants';

interface TokenizedSearchFieldProps {
  /** Array of strings used to limit a filter to. These will be provided as
   * suggestions in the underlying `SearchField`.
   */
  scopes: string[];

  /** Called when filters are successfully added or removed. */
  onFiltersChange: (filters: Filter[]) => void;
}

/** Presents a UI for entering optionally scoped terms to filter a list by. */
export const TokenizedSearchField: React.FC<TokenizedSearchFieldProps> = (
  props
) => {
  const { t } = useTranslation();
  const { scopes, onFiltersChange } = props;
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);

  // Load filters from localStorage on component mount
  useEffect(() => {
    const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters) as Filter[];
        setActiveFilters(parsedFilters);
        onFiltersChange(parsedFilters);
      } catch (e) {
        // If parsing fails, clear the storage
        localStorage.removeItem(FILTERS_STORAGE_KEY);
      }
    }
  }, []);

  // Save filters to localStorage whenever they change
  const updateFilters = (filters: Filter[]) => {
    setActiveFilters(filters);
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    onFiltersChange(filters);
  };

  const handleChange = (term: string) => {
    setCurrentSearchTerm(term);
  };

  const handleSubmit = (serializedFilter: string) => {
    const deserializedFilter = deserializeFilter(serializedFilter);

    const hasFilter = activeFilters.some(
      ({ scope, term }) =>
        scope === deserializedFilter.scope && term === deserializedFilter.term
    );

    if (!deserializedFilter.term.trim() || hasFilter) {
      setCurrentSearchTerm('');
      return;
    }

    const updatedFilters = [...activeFilters, deserializedFilter];
    setCurrentSearchTerm('');
    updateFilters(updatedFilters);
  };

  const removeFilter = (removedIndex: number) => {
    const newFilters = activeFilters.filter(
      (_, index) => removedIndex !== index
    );
    updateFilters(newFilters);
  };

  const removeAllFilters = () => {
    updateFilters([]);
  };

  return (
    <Row widths={[350, 'fill']}>
      <SearchField
        closeOnClickSuggestion={false}
        data-testid="filter-testid"
        id="filter-id"
        placeholder={t('program_search_placeholder')}
        searchButton
        value={currentSearchTerm}
        onChange={handleChange}
        onSubmit={handleSubmit}
      >
        <SearchSuggestionHeader>
          {t('tokenized_search_filter_by')}
        </SearchSuggestionHeader>
        {scopes.map((scope) => (
          <SearchSuggestion
            key={scope}
            onClick={() => {
              setCurrentSearchTerm(`${scope}${SCOPE_TERM_DELIMITER}`);
            }}
          >
            {scope}
          </SearchSuggestion>
        ))}
      </SearchField>

      <TagCloud
        data-testid="filter-field-tag-list"
        tags={serializeFilters(activeFilters)}
        onClearAllClicked={removeAllFilters}
        onTagClicked={removeFilter}
      />
    </Row>
  );
};
